import { useState, useCallback, FC } from 'react';
import { t } from 'services/i18n.service';
import {
  CLOCK_TIME_FIELD_MAX,
  DEFAULT_TIME_INPUT_MAX_LENGTH,
  TIME_DURATION_FIELD,
  TIME_INPUT_SIZE,
  TIME_INPUT_VARIANT,
  type TimeInputSize,
  type TimeInputVariant
} from 'constants/time-duration';
import {
  validateTimeInput,
  getTimeFieldMaxValue,
  getTimeFieldMaxLength,
  type TimeField
} from 'utils/validators/timeInputValidation';
import TimeDurationColumn from './TimeDurationColumn';
import ColonIcon from 'assets/icons/revamp/ColonIcon';
import { parseIntOrZero } from 'utils/number.util';
import { formatTimeTo12Hour, formatTo24 } from 'utils/time.util';
import { EMPTY_STRING } from 'constants/general';

export type { TimeInputSize, TimeInputVariant };

const PADDING_CHAR = '0';

type TimeStringValues = {
  hours: string;
  minutes: string;
  seconds: string;
};

type FieldError = { isInvalid: boolean; message: string };

const getVisibleTimeFields = (
  showHours: boolean,
  showSeconds: boolean
): TimeField[] => [
  ...(showHours ? (['hours'] as const) : []),
  'minutes',
  ...(showSeconds ? (['seconds'] as const) : [])
];

const hasEmptyVisibleTimeField = (
  values: TimeStringValues,
  showHours: boolean,
  showSeconds: boolean
): boolean =>
  getVisibleTimeFields(showHours, showSeconds).some(
    (field) => values[field].trim() === ''
  );

const getMaxValueErrorMessage = (max: number, fieldName: string) =>
  t('time_input.max_value_error', {
    max,
    field: t(`time_input.${fieldName}`)
  });

const isFieldVisible = (
  field: TimeField,
  showHours: boolean,
  showSeconds: boolean
): boolean =>
  (field === TIME_DURATION_FIELD.HOURS && showHours) ||
  field === TIME_DURATION_FIELD.MINUTES ||
  (field === TIME_DURATION_FIELD.SECONDS && showSeconds);

const buildTimeFieldError = (
  field: TimeField,
  values: TimeStringValues,
  showHours: boolean,
  showSeconds: boolean,
  maxMinutes: number
): FieldError => {
  if (!isFieldVisible(field, showHours, showSeconds)) {
    return { isInvalid: false, message: '' };
  }

  if (values[field].trim() === '') {
    return {
      isInvalid: true,
      message: t('time_input.required_error', {
        field: t(`time_input.${field}`)
      })
    };
  }

  const result = validateTimeInput(
    field,
    values[field],
    parseIntOrZero(values.hours),
    parseIntOrZero(values.minutes),
    parseIntOrZero(values.seconds),
    getMaxValueErrorMessage,
    { maxMinutes }
  );

  return {
    isInvalid: !result.isValid,
    message: result.errorMessage || ''
  };
};

const isCompleteVisibleTimeValid = (
  values: TimeStringValues,
  showHours: boolean,
  showSeconds: boolean,
  maxMinutes: number
): boolean => {
  if (hasEmptyVisibleTimeField(values, showHours, showSeconds)) {
    return false;
  }

  return getVisibleTimeFields(showHours, showSeconds).every((field) => {
    const result = validateTimeInput(
      field,
      values[field],
      parseIntOrZero(values.hours),
      parseIntOrZero(values.minutes),
      parseIntOrZero(values.seconds),
      getMaxValueErrorMessage,
      { maxMinutes }
    );
    return result.isValid;
  });
};

const buildTimeValueFromStrings = (
  values: TimeStringValues,
  showHours: boolean,
  showSeconds: boolean
) => ({
  hours: showHours ? parseIntOrZero(values.hours) : 0,
  minutes: parseIntOrZero(values.minutes),
  seconds: showSeconds ? parseIntOrZero(values.seconds) : 0
});

const padTimeField = (value: number): string =>
  value.toString().padStart(DEFAULT_TIME_INPUT_MAX_LENGTH, PADDING_CHAR);

const buildNextTimeStrings = (
  field: TimeField,
  clampedValue: string,
  current: TimeStringValues
): TimeStringValues => ({
  hours: field === TIME_DURATION_FIELD.HOURS ? clampedValue : current.hours,
  minutes:
    field === TIME_DURATION_FIELD.MINUTES ? clampedValue : current.minutes,
  seconds:
    field === TIME_DURATION_FIELD.SECONDS ? clampedValue : current.seconds
});

const buildAllFieldErrors = (
  values: TimeStringValues,
  showHours: boolean,
  showSeconds: boolean,
  maxMinutes: number
) => ({
  hours: buildTimeFieldError(
    'hours',
    values,
    showHours,
    showSeconds,
    maxMinutes
  ),
  minutes: buildTimeFieldError(
    'minutes',
    values,
    showHours,
    showSeconds,
    maxMinutes
  ),
  seconds: buildTimeFieldError(
    'seconds',
    values,
    showHours,
    showSeconds,
    maxMinutes
  )
});

const SIZE_SMALL = {
  TEXT_SIZE: '~text-xs/base',
  LABEL_SIZE: '~text-[0.65rem]/xs'
} as const;

const SIZE_LARGE = {
  TEXT_SIZE: '~text-base/xl',
  LABEL_SIZE: '~text-xs/sm`'
} as const;

type TimeChangeHandler = (
  time: {
    hours: number;
    minutes: number;
    seconds: number;
  } | null,
  formatted24HourTime: string,
  formatted12HourTime: string
) => void;

const canFormatAsClockTime = (hours: number, minutes: number): boolean =>
  hours <= CLOCK_TIME_FIELD_MAX.HOURS && minutes < CLOCK_TIME_FIELD_MAX.MINUTES;

const emitTimeChange = (
  values: TimeStringValues,
  showHours: boolean,
  showSeconds: boolean,
  maxMinutes: number,
  onChange?: TimeChangeHandler
) => {
  if (!isCompleteVisibleTimeValid(values, showHours, showSeconds, maxMinutes)) {
    onChange?.(null, '', '');
    return;
  }
  const timeValue = buildTimeValueFromStrings(values, showHours, showSeconds);
  const canFormat = canFormatAsClockTime(timeValue.hours, timeValue.minutes);
  const formatted12HourTime = canFormat
    ? formatTimeTo12Hour(`${timeValue.hours}:${timeValue.minutes}`)
    : EMPTY_STRING;
  const formatted24HourTime = canFormat
    ? formatTo24(formatted12HourTime)
    : EMPTY_STRING;
  onChange?.(
    timeValue,
    formatted24HourTime || EMPTY_STRING,
    formatted12HourTime || EMPTY_STRING
  );
};

export interface TimeDurationProps {
  /**
   * Read once on mount to seed inputs. To reset from the parent, remount this component
   * (e.g. change `key`) � subsequent prop updates are ignored.
   */
  initialValue?: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  onChange?: TimeChangeHandler;
  className?: string;
  showSeconds?: boolean;
  showHours?: boolean;
  disabled?: boolean;
  size?: TimeInputSize;
  variant?: TimeInputVariant;
  idPrefix?: string;
  showErrors?: boolean;
  showFormattedTime?: boolean;
  dataTour?: string;
  /** Minutes field max. Defaults to clock-style 60; use habit max (e.g. 999) for duration. */
  maxMinutes?: number;
}

const sizeStyles = (size: TimeInputSize) => {
  if (size === TIME_INPUT_SIZE.SMALL) {
    return {
      container: 'border rounded-md shadow-sm ~px-1.5/2.5 ~py-0.5/1.5',
      input: `~px-1.5/3 ~w-10/20 ${SIZE_SMALL.TEXT_SIZE}`,
      label: SIZE_SMALL.LABEL_SIZE
    };
  }
  return {
    container: 'border-2 rounded-lg shadow-md ~px-2/4 ~py-1/2',
    input: `~px-2.5/5 ~py-0.5/1 ~w-20/28 ${SIZE_LARGE.TEXT_SIZE}`,
    label: SIZE_LARGE.LABEL_SIZE
  };
};

const TimeDuration: FC<TimeDurationProps> = ({
  initialValue = { hours: 0, minutes: 0, seconds: 0 },
  onChange,
  className = '',
  showSeconds = false,
  showHours = true,
  disabled = false,
  size = 'large',
  variant = 'default',
  idPrefix = 'time-input',
  showErrors = false,
  showFormattedTime = false,
  dataTour,
  maxMinutes = CLOCK_TIME_FIELD_MAX.MINUTES
}) => {
  const [timeValues, setTimeValues] = useState(() => ({
    hours: padTimeField(initialValue.hours),
    minutes: padTimeField(initialValue.minutes),
    seconds: padTimeField(initialValue.seconds),
    errors: {
      hours: { isInvalid: false, message: '' },
      minutes: { isInvalid: false, message: '' },
      seconds: { isInvalid: false, message: '' }
    }
  }));

  const runValidationAndUpdateUi = useCallback(
    (field: TimeField, newValue: string) => {
      const maxLength = getTimeFieldMaxLength(field, maxMinutes);
      const nextStrings = buildNextTimeStrings(
        field,
        newValue.slice(0, maxLength),
        timeValues
      );
      setTimeValues((prev) => ({
        ...prev,
        ...nextStrings,
        errors: buildAllFieldErrors(
          nextStrings,
          showHours,
          showSeconds,
          maxMinutes
        )
      }));
      emitTimeChange(nextStrings, showHours, showSeconds, maxMinutes, onChange);
    },
    [onChange, timeValues, showHours, showSeconds, maxMinutes]
  );

  const handleFocus = useCallback(
    (field: TimeField) => {
      setTimeValues((prev) => {
        if (!prev.errors[field].isInvalid) {
          return prev;
        }

        return {
          ...prev,
          errors: {
            ...prev.errors,
            [field]: {
              ...prev.errors[field],
              message: t('time_input.max_value_error', {
                max: getTimeFieldMaxValue(field, maxMinutes),
                field: t(`time_input.${field}`)
              })
            }
          }
        };
      });
    },
    [maxMinutes]
  );

  const sizeStyle = sizeStyles(size);

  const variantClasses =
    variant === TIME_INPUT_VARIANT.PRIMARY
      ? {
          container:
            'border-primary-100 dark:border-primary-500 text-primary-300 dark:text-primary-300',
          inputText: 'text-primary-300 dark:text-secondary-dark-900',
          label: 'text-primary-300 dark:text-primary-300'
        }
      : {
          container:
            'border-gray-300 dark:border-secondary-dark-500 text-gray-800 dark:text-secondary-dark-900',
          inputText: 'text-gray-800 dark:text-secondary-dark-900',
          label: 'text-gray-500 dark:text-secondary-dark-800'
        };

  const inputBaseClasses = `time-input-number ${sizeStyle.input} border-none box-border text-center ${variantClasses.inputText} bg-transparent font-medium focus:outline-none cursor-text`;
  const minutesInputClasses =
    maxMinutes > CLOCK_TIME_FIELD_MAX.MINUTES
      ? `${inputBaseClasses} !~w-12/24`
      : inputBaseClasses;

  const id = `${idPrefix}-time-input`;

  return (
    <div
      data-tour={dataTour}
      className={`w-fit bg-white dark:bg-secondary-dark-300 flex flex-col gap-0.5 rounded-md ${className}`}>
      <div
        className={`${sizeStyle.container} ${variantClasses.container} w-fit flex items-center ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}>
        {showHours && (
          <>
            <TimeDurationColumn
              field='hours'
              id={`${id}-hours`}
              value={timeValues.hours}
              disabled={disabled}
              inputBaseClasses={inputBaseClasses}
              label={t('time_input.hours')}
              labelClassName={`${sizeStyle.label} ${variantClasses.label} transition-colors`}
              error={timeValues.errors.hours}
              onChange={(nextField, value) =>
                runValidationAndUpdateUi(nextField, value)
              }
              onFocus={handleFocus}
              showErrors={showErrors}
              variant={variant}
              maxLength={getTimeFieldMaxValue(
                TIME_DURATION_FIELD.HOURS,
                maxMinutes
              )}
            />
            <ColonIcon className='~w-4/6 h-auto' />
          </>
        )}
        <TimeDurationColumn
          field='minutes'
          id={`${id}-minutes`}
          value={timeValues.minutes}
          disabled={disabled}
          inputBaseClasses={minutesInputClasses}
          label={t('time_input.minutes')}
          labelClassName={`${sizeStyle.label} ${variantClasses.label} transition-colors`}
          error={timeValues.errors.minutes}
          onChange={(nextField, value) =>
            runValidationAndUpdateUi(nextField, value)
          }
          onFocus={handleFocus}
          showErrors={showErrors}
          variant={variant}
          maxLength={getTimeFieldMaxValue(
            TIME_DURATION_FIELD.MINUTES,
            maxMinutes
          )}
        />
        {showSeconds && (
          <>
            <ColonIcon
              className='~w-4/6 h-auto'
              fill='currentColor'
              fillDarkMode='currentColor'
            />
            <TimeDurationColumn
              field='seconds'
              id={`${id}-seconds`}
              value={timeValues.seconds}
              disabled={disabled}
              inputBaseClasses={inputBaseClasses}
              label={t('time_input.seconds')}
              labelClassName={`${sizeStyle.label} ${variantClasses.label} transition-colors`}
              error={timeValues.errors.seconds}
              onChange={(nextField, value) =>
                runValidationAndUpdateUi(nextField, value)
              }
              onFocus={handleFocus}
              showErrors={showErrors}
              variant={variant}
              maxLength={getTimeFieldMaxValue(
                TIME_DURATION_FIELD.SECONDS,
                maxMinutes
              )}
            />
          </>
        )}
      </div>
      {showFormattedTime ? (
        <p
          className={`${variantClasses.inputText} ${sizeStyle.label} font-bold self-end`}>
          {canFormatAsClockTime(
            parseIntOrZero(timeValues.hours),
            parseIntOrZero(timeValues.minutes)
          )
            ? formatTimeTo12Hour(`${timeValues.hours}:${timeValues.minutes}`)
            : EMPTY_STRING}
        </p>
      ) : null}
    </div>
  );
};

export { TimeDuration };

export const TimeDurationSmall = (props: Omit<TimeDurationProps, 'size'>) => (
  <TimeDuration {...props} size='small' />
);

export const TimeDurationLarge = (props: Omit<TimeDurationProps, 'size'>) => (
  <TimeDuration {...props} size='large' />
);
